import React, { forwardRef, useContext, useEffect, useId } from 'react'

import { Icon, TriangleUp, TriangleDown, Close, classnames } from "@cgtrader/triangle-ui/dist/src/ssr-index"
import { bool, func, arrayOf, shape, string, oneOfType, number } from 'prop-types'
import { createPortal } from 'react-dom'

import FocusTrapContext from 'Utils/FocusTrapContext'

import SearchInput from './Components/SearchInput'
import SelectOptionItem from './Components/SelectOptionItem'
import useCustomSelect from './Hooks/useCustomSelect'

const CustomSelect = forwardRef(({
  options,
  value = null,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  isLoading = false,
  className = '',
  valueClassName = '',
  searchable = false,
  isClearable = false,
  ariaLabel,
}, ref) => {
  const {
    isOpen,
    searchTerm,
    focusedIndex,
    filteredOptions,
    containerRef,
    controlRef,
    menuRef,
    inputRef,
    menuPosition,
    displayValue,
    shouldShowHoverEffect,
    setIsHovered,
    setFocusedIndex,
    handleSearchChange,
    toggleOpen,
    closeDropdown,
  } = useCustomSelect({ options, searchable, value, placeholder, disabled, onChange })

  const focusTrap = useContext(FocusTrapContext)
  const listboxId = useId()

  // The menu is portaled to document.body, so an ancestor dialog's focus trap would
  // treat its focusables as "outside" and hijack Tab. Register it as a trap ally,
  // anchored to the control so Tab order splices the menu right after this select.
  useEffect(() => {
    if (!isOpen || !focusTrap || !menuRef.current) return undefined
    return focusTrap.registerAlly(menuRef.current, controlRef.current)
  }, [isOpen, focusTrap, menuRef, controlRef])

  const handleContainerClick = () => {
    if (!disabled && !isLoading) {
      toggleOpen()
    }
  }

  const handleControlKeyDown = (event) => {
    if (disabled || isLoading || isOpen) return

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      toggleOpen()
    }
  }

  const handleOptionClick = (option) => {
    onChange(option)
    closeDropdown()
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange(null)
  }

  return (
    <div
      ref={(node) => {
        containerRef.current = node
        const forwardedRef = ref
        if (typeof forwardedRef === 'function') {
          forwardedRef(node)
        } else if (forwardedRef) {
          forwardedRef.current = node
        }
      }}
      className={classnames('custom-searchable-select', className)}
    >
      {/* Div-based control: tabIndex + combobox role + key handler are required to
          reach and open it via keyboard (native <select> semantics are not inherited). */}
      <div
        ref={controlRef}
        className={classnames(
          'custom-searchable-select__control',
          disabled && 'custom-searchable-select__control--disabled',
          shouldShowHoverEffect && 'custom-searchable-select__control--hover',
        )}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-disabled={disabled}
        aria-label={ariaLabel}
        onClick={handleContainerClick}
        onKeyDown={handleControlKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span
          className={classnames(
            'custom-searchable-select__value',
            valueClassName,
            !value && 'custom-searchable-select__placeholder',
          )}
        >
          {isLoading ? 'Loading...' : displayValue}
        </span>

        <div className="custom-searchable-select__icons">
          {isClearable && value && !disabled && !isLoading && (
            <Icon
              svgComponent={Close}
              color="dark"
              size="tiny"
              onClick={handleClear}
            />
          )}

          <Icon
            svgComponent={isOpen ? TriangleUp : TriangleDown}
            color="dark"
            className="custom-searchable-select__icon"
          />
        </div>
      </div>

      {/* Portal to document.body to avoid clipping by overflow:auto ancestors (e.g. sidebar) */}
      {isOpen && !disabled && !isLoading && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="custom-searchable-select__menu custom-searchable-select__menu--portal"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            minWidth: menuPosition.minWidth,
          }}
        >
          {searchable && (
            <SearchInput
              inputRef={inputRef}
              searchTerm={searchTerm}
              onChange={handleSearchChange}
            />
          )}

          <div
            id={listboxId}
            role="listbox"
            className={classnames(
              'custom-searchable-select__options',
              searchable && 'custom-searchable-select__options--searchable'
            )}
          >
            {filteredOptions.length === 0 ? (
              <div className="custom-searchable-select__no-options">
                No options found
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = value && value.value === option.value
                const isFocused = index === focusedIndex

                return (
                  <SelectOptionItem
                    key={option.value}
                    option={option}
                    isSelected={isSelected}
                    isFocused={isFocused}
                    searchable={searchable}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOptionClick(option)
                    }}
                    onMouseEnter={() => setFocusedIndex(index)}
                  />
                )
              })
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
})

CustomSelect.propTypes = {
  options: arrayOf(shape({
    label: string.isRequired,
    value: oneOfType([string, number]).isRequired,
  })).isRequired,
  value: shape({
    label: string.isRequired,
    value: oneOfType([string, number]).isRequired,
  }),
  onChange: func.isRequired,
  placeholder: string,
  disabled: bool,
  isLoading: bool,
  className: string,
  valueClassName: string,
  searchable: bool,
  isClearable: bool,
  ariaLabel: string,
}

export default CustomSelect
